import { Button } from '@heidi/design-system/button';
import { cn } from '@heidi/utils';
import { X } from 'lucide-react';
import { type MouseEvent } from 'react';
import { useIntl } from 'react-intl';

interface DictationCloseButtonProps {
  onClose: (event: MouseEvent<HTMLButtonElement>) => void;
  /** Offset the control across the surface's top-right edge. */
  overlapCorner?: boolean;
  /** Include an offset control in the native widget's clickable union. */
  includeInWidgetHitRect?: boolean;
  /**
   * When provided, visibility is driven by this explicit (JS) hover flag rather
   * than the default CSS `group-hover`. The native dictation widget needs this:
   * WebKit can strand the NSPanel's CSS `:hover` when the panel transitions
   * key/non-key, latching the button visible and never auto-dismissing. The JS
   * hover state is reliably reset by the native AppKit tracking area, so the
   * button hides with it. Omit it (web dictation bubble) to keep the standard
   * `group-hover` reveal.
   */
  visible?: boolean;
}

export const DictationCloseButton = ({
  onClose,
  visible,
  overlapCorner,
  includeInWidgetHitRect,
}: DictationCloseButtonProps) => {
  const intl = useIntl();

  return (
    <Button
      iconOnly
      data-widget-hit-surface={includeInWidgetHitRect || undefined}
      className={cn(
        'absolute size-4 min-w-4 rounded-full p-0 opacity-0 transition-opacity duration-200 focus:opacity-100',
        overlapCorner ? '-end-0.5 top-[-5px]' : 'end-0 top-0',
        // No `visible` prop (web bubble): reveal via CSS group-hover, as before.
        visible === undefined && 'group-hover:opacity-100',
        // Explicit `visible` (native widget): reveal off the JS hover flag.
        visible === true && 'opacity-100'
      )}
      size="md"
      onClick={onClose}
      aria-label={intl.formatMessage({
        defaultMessage: 'Close',
        id: 'rbrahOGMC3',
      })}
    >
      <X />
    </Button>
  );
};
